import { Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import React, { useState, useEffect } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { DataLake, Util } from '@finley-technologies/finley-types';

import type { GetQuickSightEmbedUrlParamsGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  useGetQuickSightConsoleEmbedUrlLazyQueryGQLType,
  useGetQuickSightDashboardEmbedUrlLazyQueryGQLType,
} from '../requests/requests.gql.generated';
import { EmbedStylingInput } from '../types';
import { QuickSightEmbedConsole } from './QuickSightEmbedConsole';
import { QuickSightEmbedDashboard } from './QuickSightEmbedDashboard';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { errorToastConfig } from 'packages/common-frontend/constants';

enum ContainerState {
  Loading,
  Authorized,
  Unauthorized,
  Error,
  DashboardNotConfigured,
  UserNotConfigured,
}

type QuickSightContainerInput = {
  containerId: string;
  embedType: DataLake.QuicksightEmbedType;
  embedUrlQueryParams?: GetQuickSightEmbedUrlParamsGQLType;
  embedStyling?: EmbedStylingInput;
};
export const QuickSightContainer = ({
  containerId,
  embedType,
  embedUrlQueryParams,
  embedStyling,
}: QuickSightContainerInput) => {
  const toast = useToast();
  const [containerState, setContainerState] = useState<ContainerState>(
    ContainerState.Loading,
  );
  const [quickSightUrl, setQuickSightUrl] = useState<string | null>(null);

  const [
    getConsoleEmbedUrl,
    {
      data: consoleEmbedUrlData,
      loading: isGetConsoleEmbedUrlLoading,
      error: consoleEmbedUrlError,
    },
  ] = useGetQuickSightConsoleEmbedUrlLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });
  const [
    getDashboardEmbedUrl,
    {
      data: dashboardEmbedUrlData,
      loading: isGetDashboardEmbedUrlLoading,
      error: dashboardEmbedUrlError,
    },
  ] = useGetQuickSightDashboardEmbedUrlLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // Manage triggering the lazy GQL queries based on embedType conditional driver
    switch (embedType) {
      case DataLake.QuicksightEmbedType.Console:
        getConsoleEmbedUrl();
        break;
      case DataLake.QuicksightEmbedType.Dashboard:
        if (!embedUrlQueryParams) {
          setQuickSightUrl(null);
          setContainerState(ContainerState.Error);
          return;
        }
        getDashboardEmbedUrl({
          variables: {
            getDashboardParams: embedUrlQueryParams,
          },
        });
        break;
      default:
        Sentry.captureException(
          `QuickSightContainer received unsupported embedType of ${embedType}`,
        );
        setQuickSightUrl(null);
        setContainerState(ContainerState.Error);
    }
  }, [
    embedType,
    embedUrlQueryParams,
    getConsoleEmbedUrl,
    getDashboardEmbedUrl,
  ]);

  useEffect(() => {
    let mounted = true;
    let errorCode: Util.Maybe<string> = null;
    let embedUrl: Util.Maybe<string> = null;

    // Set target data based on queried results
    switch (embedType) {
      case DataLake.QuicksightEmbedType.Console: {
        errorCode = consoleEmbedUrlData?.getQuickSightConsoleEmbedUrl.errorCode;
        embedUrl = consoleEmbedUrlData?.getQuickSightConsoleEmbedUrl.embedUrl;
        break;
      }
      case DataLake.QuicksightEmbedType.Dashboard: {
        errorCode =
          dashboardEmbedUrlData?.getQuickSightDashboardEmbedUrl.errorCode;
        embedUrl =
          dashboardEmbedUrlData?.getQuickSightDashboardEmbedUrl.embedUrl;
        break;
      }
      default:
        Sentry.captureException(
          `QuickSightContainer received unsupported embedType of ${embedType}`,
        );
        setQuickSightUrl(null);
        setContainerState(ContainerState.Error);
    }

    // Update states based on queried target data
    if (mounted) {
      if (errorCode) {
        switch (errorCode) {
          case DataLake.QuicksightEmbeddingErrorCodes
            .UserIsNotConfiguredForQuicksight:
            setQuickSightUrl(null);
            setContainerState(ContainerState.UserNotConfigured);
            break;
          case DataLake.QuicksightEmbeddingErrorCodes
            .DashboardIsNotConfiguredForQuicksight:
            setQuickSightUrl(null);
            setContainerState(ContainerState.DashboardNotConfigured);
            break;
          default:
            setQuickSightUrl(null);
            setContainerState(ContainerState.Unauthorized);
        }
      } else if (embedUrl) {
        setQuickSightUrl(embedUrl);
        setContainerState(ContainerState.Authorized);
      }
    }

    return () => {
      mounted = false;
    };
  }, [consoleEmbedUrlData, dashboardEmbedUrlData, embedType]);

  if (
    isGetConsoleEmbedUrlLoading ||
    isGetDashboardEmbedUrlLoading ||
    containerState === ContainerState.Loading
  ) {
    return <Skeleton height="600px" data-testid="insights-v2-loading" />;
  }

  if (containerState === ContainerState.Error) {
    const description =
      'Unable to load insights. Please contact Finley CMS for support.';
    return <InfoAlertWithDescription description={description} />;
  }

  if (containerState === ContainerState.Unauthorized) {
    const description = 'Please contact Finley CMS to enable insights access.';
    return <InfoAlertWithDescription description={description} />;
  }

  if (containerState === ContainerState.UserNotConfigured) {
    const description =
      'Please contact Finley CMS to configure user for access.';
    return <InfoAlertWithDescription description={description} />;
  }

  if (containerState === ContainerState.DashboardNotConfigured) {
    const description = 'Please contact Finley CMS to configure dashboard.';
    return <InfoAlertWithDescription description={description} />;
  }

  if (containerState !== ContainerState.Authorized) {
    Sentry.captureException(
      'QuickSightContainer should be in authorized state',
    );
    return <InfoAlertWithDescription />;
  }

  // TODO: better manage GQL errors here, can customize logic / reaction
  if (consoleEmbedUrlError || dashboardEmbedUrlError) {
    Sentry.captureException('Incurred server error when fetching QS embed url');
    return <InfoAlertWithDescription />;
  }

  if (!quickSightUrl) {
    Sentry.captureException('quickSightUrl should exist');
    return <InfoAlertWithDescription />;
  }

  switch (embedType) {
    case DataLake.QuicksightEmbedType.Console:
      return (
        <QuickSightEmbedConsole
          quickSightUrl={quickSightUrl}
          containerId={containerId}
          embedStyling={embedStyling}
        />
      );
    case DataLake.QuicksightEmbedType.Dashboard:
      return (
        <QuickSightEmbedDashboard
          quickSightUrl={quickSightUrl}
          containerId={containerId}
          embedStyling={embedStyling}
        />
      );
    default:
      Sentry.captureException(
        `QuickSightContainer received unsupported embedType of ${embedType}`,
      );
      return <InfoAlertWithDescription />;
  }
};
